import React, { CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faAngleDown, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { theme } from '../styles/theme';
import { observer } from 'mobx-react';

export interface TreeViewItem {
  domRef: React.RefObject<HTMLDivElement>;
  hasChildren: boolean;
  key: string;
  isSelected: boolean;
  isExpanded: boolean;
}

interface TreeNodeProps<T extends TreeViewItem> {
  dataItem: T;
  displayNameSelector: (item: T) => string;
  iconSelector: (item: T) => IconProp;
  indentLevel: number;
  onClick?: (dataItem: T) => void;
  onDblClick?: (dataItem: T) => void;
  onExpandoClick?: (dataItem: T) => void;
  recursiveNodeSelector: (item: T) => T[];
}

@observer
class TreeNode<T extends TreeViewItem> extends React.Component<
  TreeNodeProps<T>
> {
  constructor(props) {
    super(props);
    this.props.dataItem.domRef = React.createRef();
  }

  renderExpando(item: T, onClick: (ev: React.MouseEvent) => void) {
    if (!item.hasChildren) {
      return <span />;
    } else if (this.props.dataItem.isExpanded) {
      var icon = faAngleDown;
    } else {
      var icon = faAngleRight;
    }

    return <ExpandoIcon icon={icon} onClick={onClick} />;
  }

  render() {
    const onNodeClick = () => {
      if (this.props.onClick) {
        this.props.onClick(this.props.dataItem);
      }
    };
    const onNodeDblClick = () => {
      if (this.props.onDblClick) {
        this.props.onDblClick(this.props.dataItem);
      }
    };
    const onExpandoClick = (ev: React.MouseEvent) => {
      ev.stopPropagation();
      if (this.props.onExpandoClick) {
        this.props.onExpandoClick(this.props.dataItem);
      }
    };

    const treeNodeStyle = {
      display: 'grid',
      fontSize: '.9em',
      cursor: 'default',
      userSelect: 'none',
      marginLeft: `${this.props.indentLevel * 10}px`,
      transition: '0.1s linear',
      padding: '2px 5px',
      borderRadius: '5px',
      gridTemplateColumns: '1em auto 1fr',
      alignItems: 'center',
      backgroundColor: this.props.dataItem.isSelected
        ? theme.primaryLighterColor
        : 'unset',
    } as CSSProperties;

    return (
      <div>
        <div
          ref={this.props.dataItem.domRef}
          style={treeNodeStyle}
          onClick={onNodeClick}
          onDoubleClick={onNodeDblClick}
        >
          {this.renderExpando(this.props.dataItem, onExpandoClick)}

          <FontAwesomeIcon
            icon={this.props.iconSelector(this.props.dataItem)}
            style={{ marginRight: '5px', width: 'unset' }}
          />

          {this.props.displayNameSelector(this.props.dataItem)}
        </div>

        {this.props.dataItem.isExpanded &&
          this.props.dataItem.hasChildren &&
          (this.props.recursiveNodeSelector(this.props.dataItem) || [])
            .length == 0 && (
            <small
              style={{
                marginLeft: (this.props.indentLevel + 1) * 10,
                color: theme.grayText,
              }}
            >
              <i>Loading...</i>
            </small>
          )}

        {(
          (this.props.dataItem.isExpanded &&
            this.props.recursiveNodeSelector(this.props.dataItem)) ||
          []
        ).map(childItem => {
          return (
            <TreeNode
              key={childItem.key}
              dataItem={childItem}
              indentLevel={this.props.indentLevel + 1}
              recursiveNodeSelector={this.props.recursiveNodeSelector}
              onClick={this.props.onClick}
              onDblClick={this.props.onDblClick}
              onExpandoClick={this.props.onExpandoClick}
              displayNameSelector={this.props.displayNameSelector}
              iconSelector={this.props.iconSelector}
            />
          );
        })}
      </div>
    );
  }
}

interface ExpandoIconProps {
  icon: IconProp;
  onClick?: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const ExpandoIcon = styled<ExpandoIconProps>(
  ({ icon, onClick, children, ...rest }) => (
    <div onClick={onClick} {...rest}>
      <FontAwesomeIcon icon={icon} />
    </div>
  ),
)`
  cursor: pointer;
  display: inline-block;
  width: 0.6em;

  &:hover {
    color: ${() => theme.primaryColor};
    transition: 0.1s linear;
  }
`;

const TreeWrapper = styled.div`
  white-space: nowrap;
`;

interface TreeViewProps<T extends TreeViewItem> {
  data: T[];
  displayNameSelector: (item: T) => string;
  iconSelector: (item: T) => IconProp;
  onNodeDblClick?: (item: T) => void;
  onNodeClick?: (item: T) => void;
  onNodeExpandoClick?: (item: T) => void;
  recursiveNodeSelector: (item: T) => T[];
}

@observer
export class TreeView<T extends TreeViewItem> extends React.Component<
  TreeViewProps<T>
> {
  render() {
    if (!this.props.data) {
      return '';
    }
    return (
      <TreeWrapper>
        {this.props.data.map(dataItem => {
          return (
            <TreeNode
              key={dataItem.key}
              indentLevel={0}
              onClick={this.props.onNodeClick}
              onDblClick={this.props.onNodeDblClick}
              onExpandoClick={this.props.onNodeExpandoClick}
              dataItem={dataItem}
              recursiveNodeSelector={this.props.recursiveNodeSelector}
              iconSelector={this.props.iconSelector}
              displayNameSelector={this.props.displayNameSelector}
            />
          );
        })}
      </TreeWrapper>
    );
  }
}
